import { DeliveriesWithPageCount, DeliveryPriceEstimation, DeliveryRequest } from './models/deliveries';
import {
  CancellationReason,
  DeliveryDetailsDto,
  DeliveryDto,
  DeliveryPriceEstimationResponseDto,
  DeliveryReport,
  DeliveryReportDto,
  DeliveryTypeParam,
  OrderType,
  PastDeliveryDto
} from './dtos/deliveries';
import {
  attachDeliveriesCount,
  createResponseDtoToCreateResponse,
  deliveryDetailsDtoToPartialDelivery,
  dtoToDeliveries,
  dtoToDeliveriesTransporter,
  dtoToDeliveryPriceEstimation,
  dtoToDeliveryReport,
  estimationRequestToDto,
  filterActiveDeliveriesOnly,
  pastDeliveriesDtoToDeliveries,
  requestToDto
} from './transformers/deliveries';
import { api } from '../utils/apiHelpers';
import { sagwClient } from './clients';
import { formatDate } from '../utils/dates';
import { LatLng } from './models/locations';

export const ACTIVE_DELIVERIES_LIMIT = 50;

export function createDelivery(deliveryRequest: DeliveryRequest) {
  return sagwClient
    .post('/mot/user/deliveries', JSON.stringify(requestToDto(deliveryRequest)))
    .then(createResponseDtoToCreateResponse);
}

export function recreateDelivery(deliveryId: string) {
  return sagwClient.post(`/mot/user/deliveries/${deliveryId}/reordering`).then(createResponseDtoToCreateResponse);
}

export type StartParam = {
  id: string;
  timestamp: number;
};

export function fetchPastDeliveries({ start, limit = 30 }: { start?: StartParam; limit?: number } = {}) {
  return sagwClient
    .get<PastDeliveryDto[]>('/mot/user/deliveries', {
      params: {
        startIndexId: start?.id,
        startIndexTime: start?.timestamp,
        limit
      }
    })
    .then(pastDeliveriesDtoToDeliveries);
}

export function fetchDeliveryDetails(deliveryId: string) {
  return sagwClient
    .get<DeliveryDetailsDto>(`/mot/user/deliveries/${deliveryId}`)
    .then(deliveryDetailsDtoToPartialDelivery);
}

export function cancelDelivery(deliveryId: string) {
  return api.delete(`/v1/b2b/deliveries/${deliveryId}`);
}

export function cancelDeliveryTransporter(deliveryId: string, reason: CancellationReason) {
  return sagwClient.delete(`/mot/user/deliveries/${deliveryId}`, {
    params: {
      cancellation_reason: reason
    }
  });
}

export function searchActiveDeliveries(term: string) {
  return (
    sagwClient
      .get<DeliveryDto[]>('/mot/user/deliveries/search', { params: { filter: 'active', searchKey: term } })
      //@ts-ignore
      .then(dtoToDeliveriesTransporter)
  );
}

export function searchPastDeliveries({
  term,
  start = 0,
  limit = 30
}: {
  term: string;
  start?: number;
  limit?: number;
}) {
  return (
    sagwClient
      .get<DeliveryDto[]>('/mot/user/deliveries/search', { params: { searchKey: term, filter: 'past', start, limit } })
      //@ts-ignore
      .then(dtoToDeliveries)
  );
}

export function createDeliveriesReport({ startDate, endDate }: { startDate: Date; endDate: Date }) {
  return sagwClient
    .post<DeliveryReportDto>('/b2b/reports/deliveries', {
      start_date: formatDate(startDate, 'yyyy-MM-dd'),
      end_date: formatDate(endDate, 'yyyy-MM-dd')
    })
    .then(dtoToDeliveryReport);
}

export function getDeliveriesReport(id: string): Promise<DeliveryReport> {
  return sagwClient.get<DeliveryReportDto>(`/b2b/reports/deliveries/${id}`).then(dtoToDeliveryReport);
}

export enum ACTIVE_DELIVERY_TYPE {
  MARKETPLACE = 4,
  OTP = 1
}

// fetching all active deliveries with filtering out the scheduled ones
export const fetchActiveDeliveriesFromTransporter = async (
  from = 0,
  activeDeliveryType: ACTIVE_DELIVERY_TYPE = ACTIVE_DELIVERY_TYPE.MARKETPLACE
): Promise<DeliveriesWithPageCount> => {
  return sagwClient
    .get<DeliveryDto[]>('/mot/user/deliveries/active', {
      params: {
        id: activeDeliveryType,
        from,
        limit: ACTIVE_DELIVERIES_LIMIT,
        deliveryType: DeliveryTypeParam.INSTANT
      }
    })
    .then(dtoToDeliveriesTransporter)
    .then(filterActiveDeliveriesOnly);
};

export const fetchScheduledDeliveriesFromTransporter = async (
  from = 0,
  activeDeliveryType: ACTIVE_DELIVERY_TYPE = ACTIVE_DELIVERY_TYPE.MARKETPLACE
): Promise<DeliveriesWithPageCount> => {
  return sagwClient
    .get<DeliveryDto[]>('/mot/user/deliveries/active', {
      params: {
        id: activeDeliveryType,
        from,
        limit: ACTIVE_DELIVERIES_LIMIT,
        deliveryType: DeliveryTypeParam.SCHEDULED
      }
    })
    .then(dtoToDeliveriesTransporter)
    .then(attachDeliveriesCount);
};

export function fetchEstimatedDeliveryPrice(
  orderType: OrderType,
  pickupLatLng: LatLng,
  dropoffLatLng: LatLng
): Promise<DeliveryPriceEstimation> {
  return sagwClient
    .post<DeliveryPriceEstimationResponseDto>(
      '/mot/user/deliveries/estimate',
      estimationRequestToDto(orderType, pickupLatLng, dropoffLatLng)
    )
    .then(dtoToDeliveryPriceEstimation);
}

export function validateDeliveryZone(locationLatLng: LatLng): Promise<boolean> {
  return sagwClient.get<boolean>('/mot/user/deliveries/validate/zone', {
    params: {
      latitude: locationLatLng.lat,
      longitude: locationLatLng.lng
    }
  });
}
